'use client';

import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { AlertCircle, WandSparkles } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';

// First, update the ChatMessage interface to include the aiTask property
export interface ChatMessage {
  user: boolean;
  msg: string;
  created: number;
  system?: boolean; // For system messages
  aiTask?: boolean; // Added aiTask flag for review/rating requests
  isLoading?: boolean;
  data?: {
    message: string;
    characters: Array<{
      nm: string;
      desc: string;
    }>;
  };
}

interface ChatMessagesProps {
  messages: ChatMessage[] | null | undefined;
  onCharacterSelect?: (characterName: string) => void;
}

// Then update the component to render the WandSparkles icon for aiTask messages
const ChatMessages: React.FC<ChatMessagesProps> = ({ messages, onCharacterSelect }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [scrollToBottom, messages]);

  const renderCharacterButtons = (characters: Array<{ nm: string; desc: string }>) => {
    if (!onCharacterSelect) return null;
    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {characters.map((character) => (
          <Button key={character.nm} variant="outline" size="sm" onClick={() => onCharacterSelect(character.nm)}>
            <WandSparkles className="mr-2 h-4 w-4" />
            Chat with {character.nm}
          </Button>
        ))}
      </div>
    );
  };

  if (!messages || messages.length === 0) {
    return <div className="text-center text-gray-500 py-4">No messages yet. Start a conversation!</div>;
  }

  return (
    <div className="flex flex-col space-y-4 p-4 max-h-96 overflow-y-auto">
      {messages.map((message, index) => (
        <div key={index} className={`flex ${message.user ? 'justify-end' : 'justify-start'}`}>
          <div
            className={`rounded-lg p-3 max-w-[90%] ${
              message.system
                ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                : message.user
                ? 'bg-blue-500 text-white'
                : 'bg-gray-200 text-gray-800'
            }`}
          >
            {message.system ? (
              <div className="flex items-start">
                <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-sm">{message.msg}</p>
              </div>
            ) : message.user ? (
              <div className="flex items-start">
                {message.aiTask && <WandSparkles className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0 text-white" />}
                <p className="text-sm">{message.msg}</p>
              </div>
            ) : message.isLoading ? (
              <div className="flex items-center space-x-2">
                <div className="animate-pulse flex space-x-1">
                  <div className="h-2 w-2 bg-gray-400 rounded-full"></div>
                  <div className="h-2 w-2 bg-gray-400 rounded-full"></div>
                  <div className="h-2 w-2 bg-gray-400 rounded-full"></div>
                </div>
                <p className="text-sm text-gray-500">AI is thinking...</p>
              </div>
            ) : (
              <ReactMarkdown className="text-sm prose dark:prose-invert max-w-none">{message.msg}</ReactMarkdown>
            )}
            <p
              className={`text-xs mt-1 ${
                message.system ? 'text-yellow-600' : message.user ? 'text-gray-200' : 'text-gray-500'
              }`}
            >
              {format(new Date(message.created * 1000), 'd MMM yy, h:mmaaa')}
            </p>
            {!message.user &&
              !message.system &&
              message.data &&
              message.data.characters &&
              renderCharacterButtons(message.data.characters)}
          </div>
        </div>
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatMessages;
